import { create } from "zustand";
import type { BotModal, CommandChoice, InteractionState, MessageAuthor, RealtimeEvent, Uuid } from "@shared/api";
import { realtime } from "@shared/realtime";

/** Бот должен ответить за три секунды — дальше Discord пишет «Приложение не ответило». */
export const RESPOND_WITHIN = 3000;

/**
 * Свой вызов команды, пока бот не ответил. `sent` — ушёл, ответа нет; `thinking` —
 * бот взял время подумать («думает…» до самого ответа); `silent` — три секунды
 * прошли, ответа нет.
 */
export interface Call {
  id: Uuid;
  channelId: Uuid;
  /** Имя команды; у нажатия без команды — пусто. */
  name: string;
  /** Команда поля ввода — с косой чертой; команда меню — без. */
  slash?: boolean;
  bot: MessageAuthor;
  at: number;
  state: "sent" | "thinking" | "silent";
}

/**
 * Своё нажатие кнопки или выбор в списке: `sent` — ушло, на компоненте крутится
 * колесо; `failed` — три секунды без ответа, под рядом «Ошибка взаимодействия»,
 * как в Discord.
 */
export interface Press {
  id: Uuid;
  messageId: Uuid;
  customId: string;
  channelId: Uuid;
  bot: MessageAuthor;
  at: number;
  state: "sent" | "failed";
}

/** Окно бота, открытое на свой вызов: ответ на него уйдёт по этому вызову. */
export interface OpenModal {
  interactionId: Uuid;
  channelId: Uuid;
  modal: BotModal;
  /** Чей бот: после ответа в окне ждём его так же, как ответа на команду. */
  bot?: MessageAuthor;
}

interface Calls {
  calls: Record<Uuid, Call>;
  presses: Record<Uuid, Press>;
  /** Варианты автодополнения по вызову типа 4. */
  choices: Record<Uuid, CommandChoice[]>;
  modal: OpenModal | null;
  /** Эфемерные ответы, которые убрали «Скрыть»: в ленте их больше нет. */
  dismissed: Set<Uuid>;
  sent: (call: Omit<Call, "at" | "state">) => void;
  pressed: (press: Omit<Press, "at" | "state">) => void;
  drop: (id: Uuid) => void;
  dismiss: (messageId: Uuid) => void;
  closeModal: () => void;
}

/**
 * Вызовы команд, нажатия и эфемерные ответы — только у себя и до перезагрузки, как
 * в Discord. Стор переписки их не касается: эфемерный ответ лежит в ленте обычным
 * сообщением с пометкой, а здесь — что убрать и чего ещё ждать.
 */
export const useInteractions = create<Calls>((set, get) => ({
  calls: {},
  presses: {},
  choices: {},
  modal: null,
  dismissed: new Set(),
  sent: (call) => {
    listen();
    // Быстрый бот отвечает раньше, чем вернулся сам вызов: тогда ждать нечего.
    const early = before.get(call.id);
    before.delete(call.id);
    if (early && early.state !== "thinking") return;
    const state = early?.state === "thinking" ? "thinking" : "sent";
    set((now) => ({ calls: { ...now.calls, [call.id]: { ...call, at: Date.now(), state } } }));
    window.setTimeout(() => {
      const now = get().calls[call.id];
      if (now?.state === "sent") set((state) => ({ calls: { ...state.calls, [call.id]: { ...now, state: "silent" } } }));
    }, RESPOND_WITHIN);
  },
  pressed: (press) => {
    listen();
    const early = before.get(press.id);
    before.delete(press.id);
    // Прежняя ошибка на этом сообщении уходит с новым нажатием.
    const kept = Object.fromEntries(
      Object.entries(get().presses).filter(([, item]) => item.messageId !== press.messageId),
    );
    if (early) {
      set({ presses: kept });
      if (early.state === "thinking") thinking({ ...press, at: Date.now(), state: "sent" });
      return;
    }
    set({ presses: { ...kept, [press.id]: { ...press, at: Date.now(), state: "sent" } } });
    window.setTimeout(() => {
      const now = get().presses[press.id];
      if (now?.state === "sent")
        set((state) => ({ presses: { ...state.presses, [press.id]: { ...now, state: "failed" } } }));
    }, RESPOND_WITHIN);
  },
  drop: (id) =>
    set((state) => {
      const { [id]: _, ...calls } = state.calls;
      const { [id]: __, ...presses } = state.presses;
      return { calls, presses };
    }),
  dismiss: (messageId) => set((state) => ({ dismissed: new Set(state.dismissed).add(messageId) })),
  closeModal: () => set({ modal: null }),
}));

/** Нажатие, на которое бот «думает», — строкой «думает…», как вызов команды. */
function thinking(press: Press) {
  useInteractions.setState((state) => ({
    calls: {
      ...state.calls,
      [press.id]: { id: press.id, channelId: press.channelId, name: "", bot: press.bot, at: Date.now(), state: "thinking" },
    },
  }));
}

let listening = false;

/** События о вызовах, пришедшие раньше ответа на сам вызов. */
const before = new Map<Uuid, InteractionState>();

/** Слушать события вызовов — с открытия поля ввода и до конца сеанса. */
export function listen() {
  if (listening) return;
  listening = true;
  realtime.on((event: RealtimeEvent) => {
    if (event.context !== "interaction" || event.action !== "update") return;
    const update = event.payload as InteractionState;
    // Окно и варианты приходят и на то, чего здесь не ждали: вызов из меню, окно из окна.
    if (update.state === "modal" && update.modal) {
      const { calls, presses } = useInteractions.getState();
      const bot = (calls[update.interaction_id] ?? presses[update.interaction_id])?.bot;
      useInteractions.setState({
        modal: { interactionId: update.interaction_id, channelId: update.channel_id, modal: update.modal, bot },
      });
    }
    if (update.state === "choices") {
      useInteractions.setState((state) => ({
        choices: { ...state.choices, [update.interaction_id]: update.choices ?? [] },
      }));
      return;
    }

    const { calls, presses, drop } = useInteractions.getState();
    const call = calls[update.interaction_id];
    const press = presses[update.interaction_id];
    if (press) {
      drop(press.id);
      if (update.state === "thinking") thinking(press);
      return;
    }
    if (!call) {
      before.set(update.interaction_id, update);
      return;
    }
    if (update.state === "thinking")
      useInteractions.setState({ calls: { ...calls, [call.id]: { ...call, state: "thinking" } } });
    else drop(call.id);
  });
}
